'use client'

import type { Data } from '@repo/strapi-types'
import { ChevronDown } from '@/components/icons/ChevronDown'
import { FeatureCard } from '@/components/page-builder/global/featureRoadmap/FeatureCard'
import { useCollapsibleCategories } from '@/components/page-builder/global/featureRoadmap/useCollapsibleCategories'
import Heading from '@/components/typography/Heading'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/styles'

type FeatureRoadmapMobileProps = {
	readonly categories: Data.Component<'shared.roadmap-category'>[]
	readonly column: 'primary' | 'secondary'
}

export function FeatureRoadmapMobile({ categories, column }: FeatureRoadmapMobileProps) {
	const { openCategories, toggleCategory } = useCollapsibleCategories(categories)

	return (
		<div className="bg-white rounded-xl">
			<div className="flex flex-col gap-15">
				{categories.map((category) => {
					if (!category || !category.id) return null

					const categoryId = category.id
					const isOpen = openCategories[categoryId] ?? false
					const items =
						column === 'primary' ? category.primaryColumnItems : category.secondaryColumnItems

					if (!items || items.length === 0) return null

					return (
						<Collapsible
							key={categoryId}
							open={isOpen}
							onOpenChange={() => toggleCategory(categoryId)}
							className="w-full"
						>
							{/* Category Header */}
							<CollapsibleTrigger className="group inline-flex items-center gap-2 cursor-pointer py-2.5 px-4 hover:opacity-70 transition-opacity">
								<Heading tag="h2" fontSize="headingMedium">
									{category.title}
								</Heading>
								<ChevronDown
									className={cn('size-5 transition-transform duration-200', isOpen && 'rotate-180')}
								/>
							</CollapsibleTrigger>

							{/* Category Content */}
							<CollapsibleContent>
								<div className="flex flex-col gap-5 px-4 pt-5">
									{items.map((item) => {
										if (!item) return null
										return (
											<FeatureCard
												key={item.id}
												item={item}
												variant={column === 'secondary' ? 'secondary' : 'primary'}
											/>
										)
									})}
								</div>
							</CollapsibleContent>
						</Collapsible>
					)
				})}
			</div>
		</div>
	)
}
